import { META_TAGS } from '@hub/libs/constants';
import { NextSeo } from 'next-seo';
import { htmlDecode } from '@hub/libs/dom-parser.helper';
import { useTranslation } from 'next-i18next';
import type { LanguageAlternate } from '@hub/models/data';
import { usePreparedOpenGraphData } from './hooks/usePreparedOpenGraphData';
import { useBaseUrl } from './hooks/useBaseUrl';
import { usePagination } from '@hub/common/hooks/usePagination';
import { useRouter } from 'next/router';

type SeoMetaProps = {
  metaTitle?: string;
  metaDescription?: string;
  categoryName: string;
  languageAlternates: LanguageAlternate[];
  totalPages: number;
  productImage: string | null;
  webTrendCGNKey?: string;
  webTrendCGSContent?: string;
  canonical?: string;
  noIndexState?: boolean;
  noFollow?: boolean;
  marketingCloudTags?: { name: string; content: string }[];
};

export const SeoMeta = ({
  categoryName,
  metaTitle,
  metaDescription,
  languageAlternates,
  totalPages,
  productImage,
  webTrendCGNKey,
  webTrendCGSContent,
  canonical,
  noIndexState,
  noFollow,
  marketingCloudTags,
}: SeoMetaProps) => {
  const { t } = useTranslation('category/categoryPage');
  const { categoryL1 } = useRouter().query;
  const baseUrl = useBaseUrl();
  const openGraphData = usePreparedOpenGraphData(baseUrl, categoryName, productImage);
  const { page } = usePagination();
  const noIndex = noIndexState || page > 1;

  // Marketing Cloud parameters
  const defaultMarketingCloudTags = [
    {
      name: 'sis-tag_page-type',
      content: 'Shop',
    },
    {
      name: 'sis-tag_page-subtype',
      content: 'Product Overview Page',
    },
  ];

  return (
    <NextSeo
      openGraph={openGraphData}
      title={htmlDecode(
        metaTitle ||
          t('GENERAL.CATEGORY_META_TITLE', {
            category: categoryName,
          })
      )}
      noindex={noIndex}
      nofollow={noFollow}
      canonical={noIndex ? undefined : canonical || baseUrl}
      languageAlternates={!noIndex && languageAlternates.length > 0 ? languageAlternates : undefined}
      description={
        metaDescription ||
        t('GENERAL.CATEGORY_META_DESC', {
          category: categoryName,
        })
      }
      additionalMetaTags={[
        {
          name: 'WT.cg_n',
          content:
            META_TAGS.WEB_TRENDS['WT.cg_n'][
              (webTrendCGNKey as keyof (typeof META_TAGS.WEB_TRENDS)['WT.cg_n']) ||
                (categoryL1 as keyof (typeof META_TAGS.WEB_TRENDS)['WT.cg_n'])
            ],
        },
        { name: 'WT.cg_s', content: webTrendCGSContent || META_TAGS.WEB_TRENDS['WT.cg_s'] },
        { name: 'test.pages.total', content: String(totalPages) },
        ...(marketingCloudTags || defaultMarketingCloudTags),
      ]}
    />
  );
};
